import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Lock, Unlock, Save } from 'lucide-react';
import { promptsAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const ManagePrompts = () => {
  const [prompts, setPrompts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'llm',
    is_premium: true,
    tags: '',
    prompt_content: '',
    image_url: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [promptsRes, categoriesRes] = await Promise.all([
        promptsAPI.getAll(),
        promptsAPI.getCategories(),
      ]);
      setPrompts(promptsRes.data);
      setCategories(categoriesRes.data.filter(c => c.slug !== 'all'));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPrompts = prompts.filter((prompt) =>
    prompt.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openModal = (prompt = null) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setFormData({
        title: prompt.title,
        description: prompt.description,
        category: prompt.category,
        is_premium: prompt.is_premium,
        tags: prompt.tags?.join(', ') || '',
        prompt_content: prompt.prompt_content || '',
        image_url: prompt.image_url || '',
      });
    } else {
      setEditingPrompt(null);
      setFormData({
        title: '',
        description: '',
        category: 'llm',
        is_premium: true,
        tags: '',
        prompt_content: '',
        image_url: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const tagsArray = formData.tags.split(',').map((t) => t.trim()).filter(Boolean);
      
      const data = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        is_premium: formData.is_premium,
        tags: tagsArray,
        prompt_content: formData.prompt_content,
        image_url: formData.image_url || null,
      };

      if (editingPrompt) {
        await promptsAPI.update(editingPrompt.id, data);
      } else {
        await promptsAPI.create(data);
      }
      
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save prompt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;
    
    try {
      await promptsAPI.delete(id);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete prompt');
    }
  };

  const togglePremium = async (id) => {
    try {
      await promptsAPI.togglePremium(id);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to toggle premium');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Manage Prompts</h1>
          <p className="text-gray-400">Create and manage AI prompts</p>
        </div>
        <Button onClick={() => openModal()} className="bg-purple-600 hover:bg-purple-700">
          <Plus size={18} className="mr-2" />
          Add Prompt
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
        <Input
          type="text"
          placeholder="Search prompts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-[#1a1a2e] border-gray-700 text-white placeholder:text-gray-500"
        />
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        /* Prompts Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPrompts.map((prompt) => (
            <div
              key={prompt.id}
              className="bg-[#1a1a2e] rounded-xl p-4 border border-gray-800 hover:border-purple-500/50 transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">{prompt.title}</h3>
                  <p className="text-gray-400 text-sm line-clamp-2">{prompt.description}</p>
                </div>
                <button
                  onClick={() => togglePremium(prompt.id)}
                  className={`p-2 rounded-lg transition-colors ${prompt.is_premium ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'}`}
                >
                  {prompt.is_premium ? <Lock size={16} /> : <Unlock size={16} />}
                </button>
              </div>

              <div className="flex flex-wrap gap-1 mb-4">
                {prompt.tags?.slice(0, 3).map((tag, index) => (
                  <Badge key={index} className="text-xs bg-gray-700 text-gray-300">
                    {tag}
                  </Badge>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <Badge className={prompt.is_premium ? 'bg-purple-600' : 'bg-green-600'}>
                  {prompt.is_premium ? 'Premium' : 'Free'}
                </Badge>
                <div className="flex gap-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => openModal(prompt)}
                    className="text-gray-400 hover:text-white h-8 w-8"
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDelete(prompt.id)}
                    className="text-gray-400 hover:text-red-500 h-8 w-8"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-gray-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingPrompt ? 'Edit Prompt' : 'Add New Prompt'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label className="text-gray-400">Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-[#12121a] border-gray-700 text-white"
                placeholder="Prompt title"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-[#12121a] border-gray-700 text-white min-h-[80px]"
                placeholder="Prompt description"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Prompt Content</Label>
              <Textarea
                value={formData.prompt_content}
                onChange={(e) => setFormData({ ...formData, prompt_content: e.target.value })}
                className="bg-[#12121a] border-gray-700 text-white min-h-[120px] font-mono"
                placeholder="The actual prompt content that users will copy..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="bg-[#12121a] border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a2e] border-gray-700">
                    {categories.map((cat) => (
                      <SelectItem key={cat.slug} value={cat.slug} className="text-white hover:bg-purple-600/30">
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400">Image URL (Optional)</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="bg-[#12121a] border-gray-700 text-white"
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Tags (comma separated)</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="bg-[#12121a] border-gray-700 text-white"
                placeholder="Premium, Social Media, Marketing"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-gray-400">Premium Content</Label>
              <Switch
                checked={formData.is_premium}
                onCheckedChange={(checked) => setFormData({ ...formData, is_premium: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)} className="border-gray-700 text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
              <Save size={16} className="mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagePrompts;
